import React from 'react';

import { SLICE_REGISTRY } from 'components/storefront/utils/constants';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';

/**
 * A dialog for adding a new slice to the storefront configuration.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {Function} props.onClose - Callback function when the dialog is closed
 * @param {Function} props.onSelect - Callback function when a slice is selected
 * @param {number} props.position - The position in the configuration to add the new slice
 * @returns {React.ReactElement} A dialog component for adding a new slice to the storefront configuration
 */
function AddSliceDialog({ isOpen, onClose, onSelect, position }) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Add New Slice</DialogTitle>
                    <DialogDescription>Select a slice type to add at position {position + 1}.</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-8">
                    {Object.entries(SLICE_GROUPS).map(([groupName, sliceComponents]) => (
                        <div key={groupName} className="flex flex-col gap-4">
                            <h3 className="mb-0 text-lg font-medium capitalize">
                                {groupName}{' '}
                                <span className="text-sm ext-muted-foreground font-normal lowercase">
                                    ({sliceComponents.length} variations)
                                </span>
                            </h3>
                            <div className="grid grid-cols-3 gap-4">
                                {sliceComponents.map((SliceComponent) => (
                                    <button
                                        key={SliceComponent.displayName}
                                        className="flex flex-col items-center gap-6 w-full p-3 border rounded-md bg-gray-100 transition-colors hover:border-primary hover:bg-border hover:cursor-pointer "
                                        onClick={() => onSelect(SliceComponent.displayType)}
                                    >
                                        <div className="flex-1 p-2 rounded-md bg-background">
                                            <img
                                                src={SliceComponent.thumbnail}
                                                alt={SliceComponent.displayName}
                                                className="object-contain w-full h-16"
                                            />
                                        </div>
                                        <p className="font-medium">{SliceComponent.displayName}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Groups slice components from SLICE_REGISTRY by their categories for UI organization.
 * This constant is automatically generated from the registry in order to keep configuration data
 * within each slice component.
 *
 * @type {Object.<string, Array.<React.ComponentType>>}
 * @example
 * // Example structure:
 * {
 *   'headers': [HeaderSlice1, HeaderSlice2],
 *   'footers': [FooterSlice1],
 *   'other': [MiscSlice1, MiscSlice2]
 * }
 */
const SLICE_GROUPS = Object.values(SLICE_REGISTRY).reduce((groups, SliceComponent) => {
    const groupName = `${SliceComponent.category}${SliceComponent.category !== 'other' ? 's' : ''}`;

    if (!groups[groupName]) {
        groups[groupName] = [];
    }

    groups[groupName].push(SliceComponent);

    return groups;
}, {});

export default AddSliceDialog;
